import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import { useAuth } from '../context/AuthContext';
import { useStores } from '../context/StoresContext';
import AddStoreModal from '../components/AddStoreModal';
import StoresTable from '../components/StoresTable';

const statCards = [
  {
    title: 'Total Customers',
    value: '--',
    icon: PeopleOutline,
    color: '#6C63FF',
  },
  {
    title: 'Total Orders',
    value: '--',
    icon: ReceiptLongOutlined,
    color: '#00D9A6',
  },
  {
    title: 'Revenue',
    value: '--',
    icon: TrendingUpRounded,
    color: '#FF9800',
  },
];

const Dashboard = () => {
  const { user } = useAuth();
  const { stores, loading: storesLoading, refreshStores } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const [addStoreOpen, setAddStoreOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Handle OAuth redirect query params
  useEffect(() => {
    if (searchParams.get('shopify_connected') === 'true') {
      setSnackbar({
        open: true,
        message: 'Shopify store connected successfully!',
        severity: 'success',
      });
      searchParams.delete('shopify_connected');
      setSearchParams(searchParams, { replace: true });
      refreshStores();
    }

    const shopifyError = searchParams.get('shopify_error');
    if (shopifyError) {
      setSnackbar({
        open: true,
        message: `Shopify connection failed: ${shopifyError}`,
        severity: 'error',
      });
      searchParams.delete('shopify_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshStores]);

  const handleStoreDisconnected = useCallback(
    (storeId) => {
      refreshStores();
      setSnackbar({
        open: true,
        message: 'Store disconnected',
        severity: 'info',
      });
    },
    [refreshStores]
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Welcome back, {user?.name?.split(' ')[0]}
      </Typography>
      {user?.last_login && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <AccessTimeRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Last login: {new Date(user.last_login).toLocaleString()}
          </Typography>
        </Box>
      )}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Here's an overview of your business
      </Typography>

      {/* Stat Cards */}
      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid key={card.title} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 40px ${alpha(card.color, 0.15)}`,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: alpha(card.color, 0.1),
                      color: card.color,
                    }}
                  >
                    <card.icon />
                  </Avatar>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {card.title}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Connected Stores — visible to all, but only admins can add/modify */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 5,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Connected Stores
        </Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setAddStoreOpen(true)}
          >
            Add Store
          </Button>
        )}
      </Box>

      {storesLoading ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading stores...</Typography>
          </CardContent>
        </Card>
      ) : (
        <StoresTable
          stores={stores}
          onStoreDisconnected={handleStoreDisconnected}
          readOnly={user?.role !== 'admin'}
        />
      )}

      {user?.role === 'admin' && (
        <AddStoreModal open={addStoreOpen} onClose={() => setAddStoreOpen(false)} />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
